import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ModalHeader, ModalBody, Label, Input, Button } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { FileSelectorService } from '../../../../components/FileSelector/FileSelector.service';
import { useStateSelector } from '../../../../store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark,
    faChevronRight,
    faChevronDown,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { VDRFolderView } from '../../../../slices/marketplace/data-room/models/vdr-folder-view.model';
import './VDRUploadFileModal.scss';

interface FolderNode {
    name: string;
    fullPath: string;
    children: Map<string, FolderNode>;
}

interface VDRUploadFileModalProps {
    isVisible: boolean;
    onClose: () => void;
    onUpload: (temporaryFileId: number, folderPath: string, fileName: string) => Promise<void>;
    rootFolder?: VDRFolderView;
    currentFolderPath: string;
}

const VDRUploadFileModal: React.FC<VDRUploadFileModalProps> = ({
    isVisible,
    onClose,
    onUpload,
    rootFolder,
    currentFolderPath,
}) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [selectedFolderPath, setSelectedFolderPath] = useState<string | null>(null);
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [addingToPath, setAddingToPath] = useState<string>('');
    const [customFolders, setCustomFolders] = useState<string[]>([]);
    const [fileSelectorService] = useState(new FileSelectorService(axios));
    const hasInitializedExpanded = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const ADD_NEW_FOLDER_KEY = '__ADD_NEW__';

    const buildFolderTree = (folder: VDRFolderView, customPaths: string[]): Map<string, FolderNode> => {
        const root = new Map<string, FolderNode>();

        const addFolderToTree = (path: string, currentLevel: Map<string, FolderNode>) => {
            const parts = path.split('/').filter((p) => p.trim());
            let currentPath = '';

            parts.forEach((part, index) => {
                currentPath += (currentPath ? '/' : '') + part;

                if (!currentLevel.has(part)) {
                    currentLevel.set(part, {
                        name: part,
                        fullPath: currentPath,
                        children: new Map<string, FolderNode>(),
                    });
                }

                currentLevel = currentLevel.get(part)!.children;
            });
        };

        const processFolder = (f: VDRFolderView) => {
            if (f.fullPath) {
                addFolderToTree(f.fullPath, root);
            }
            f.subFolders?.forEach((sub) => processFolder(sub));
        };

        if (folder) {
            processFolder(folder);
        }

        customPaths.forEach((path) => addFolderToTree(path, root));

        return root;
    };

    const { folderTree, allFolderPaths } = useMemo(() => {
        const tree = rootFolder ? buildFolderTree(rootFolder, customFolders) : new Map<string, FolderNode>();
        const paths = new Set<string>();

        const collectPaths = (nodes: Map<string, FolderNode>) => {
            nodes.forEach((node) => {
                paths.add(node.fullPath);
                collectPaths(node.children);
            });
        };
        collectPaths(tree);

        return { folderTree: tree, allFolderPaths: paths };
    }, [rootFolder, customFolders]);

    useEffect(() => {
        if (!hasInitializedExpanded.current && allFolderPaths.size > 0) {
            setExpandedFolders(new Set(allFolderPaths));
            hasInitializedExpanded.current = true;
        }
    }, [allFolderPaths]);

    useEffect(() => {
        if (isVisible) {
            // Only pre-select if currentFolderPath is provided and not empty
            setSelectedFolderPath(currentFolderPath ? currentFolderPath : null);
        }
    }, [isVisible, currentFolderPath]);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleSelectFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = async () => {
        if (!selectedFile || !fileName.trim() || selectedFolderPath === null) {
            return;
        }

        setIsUploading(true);
        try {
            const temporaryFileId = await fileSelectorService.uploadFile(selectedFile);
            await onUpload(temporaryFileId, selectedFolderPath, fileName.trim());
            handleClose();
        } catch (error) {
            console.error('Upload failed:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setFileName('');
        setSelectedFolderPath(null);
        setNewFolderName('');
        setAddingToPath('');
        setCustomFolders([]);
        hasInitializedExpanded.current = false;
        onClose();
    };

    const toggleFolder = (path: string) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedFolders(newExpanded);
    };

    const addSubFolder = (folderName: string, parentPath: string) => {
        const newPath = parentPath ? `${parentPath}/${folderName.trim()}` : folderName.trim();
        setCustomFolders([...customFolders, newPath]);
        setAddingToPath('');
        setNewFolderName('');

        const newExpanded = new Set(expandedFolders);
        if (parentPath) {
            newExpanded.add(parentPath);
        }
        newExpanded.add(newPath);
        setExpandedFolders(newExpanded);
    };

    const renderAddFolderForm = (parentPath: string) => {
        return (
            <div className="add-folder-form">
                <span className="folder-chevron-spacer"></span>
                <Input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name"
                    autoFocus
                />
                <FontAwesomeIcon
                    className="btn-icon btn-icon-confirm"
                    onClick={() => {
                        if (newFolderName.trim()) {
                            addSubFolder(newFolderName, parentPath);
                        }
                    }}
                    icon={faCheck}
                />
                <FontAwesomeIcon
                    className="btn-icon btn-icon-cancel"
                    onClick={() => {
                        setAddingToPath('');
                        setNewFolderName('');
                    }}
                    icon={faXmark}
                />
            </div>
        );
    };

    const renderFolderTree = (
        nodes: Map<string, FolderNode>,
        level: number = 0
    ): JSX.Element[] => {
        const elements: JSX.Element[] = [];

        nodes.forEach((node) => {
            const isExpanded = expandedFolders.has(node.fullPath);
            const hasChildren = node.children.size > 0;
            const isAddingHere = addingToPath === node.fullPath;
            const isSelected = selectedFolderPath === node.fullPath;

            elements.push(
                <div key={node.fullPath}>
                    <div
                        className="folder-item"
                        style={{ paddingLeft: `${level * 20}px` }}>
                        <span className="folder-content">
                            {hasChildren ? (
                                <FontAwesomeIcon
                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                    className="folder-chevron"
                                    onClick={() => toggleFolder(node.fullPath)}
                                />
                            ) : (
                                <span className="folder-chevron-spacer"></span>
                            )}
                            <span className="folder-name">{node.name}</span>
                        </span>
                        <button
                            className="btn btn-primary select-folder-btn"
                            onClick={() => setSelectedFolderPath(node.fullPath)}>
                            {isSelected ? 'Selected' : 'Select'}
                        </button>
                    </div>

                    {(isExpanded || !hasChildren) && (
                        <>
                            {isExpanded && hasChildren && (
                                <>{renderFolderTree(node.children, level + 1)}</>
                            )}

                            <div
                                className="add-subfolder-wrapper"
                                style={{ paddingLeft: `${(level + 1) * 20}px` }}>
                                {isAddingHere ? (
                                    renderAddFolderForm(node.fullPath)
                                ) : (
                                    <button
                                        className="btn-link add-subfolder-link"
                                        onClick={() => setAddingToPath(node.fullPath)}>
                                        <span className="folder-chevron-spacer"></span>
                                        Add Sub Folder
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            );
        });

        return elements;
    };

    const isFormValid = selectedFile && fileName.trim() && selectedFolderPath !== null;

    return (
        <div className="vdr-upload-file-modal-container">
            <Rodal
                visible={isVisible}
                onClose={handleClose}
                animation="slideUp"
                className="vdr-upload-file-modal"
                showMask={true}
                width={650}
                showCloseButton={true}>
                <ModalHeader>
                    <b>Upload File to VDR</b>
                </ModalHeader>
                <ModalBody>
                    <div className="vdr-upload-form">
                        <div className="form-group">
                            <label>File Name</label>
                            <Input
                                type="text"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter file name"
                                className="form-control"
                            />
                        </div>

                        <div className="form-group">
                            <div className="file-selector">
                                <label
                                    className="link-button"
                                    onClick={handleSelectFileClick}>
                                    Select File
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                                    hidden
                                    onChange={handleFileInputChange}
                                />
                                {selectedFile && (
                                    <Input
                                        type="text"
                                        value={selectedFile.name}
                                        disabled={true}
                                        className="form-control file-display"
                                    />
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Select Destination Folder</label>
                            <div className="folder-tree-container">
                                {renderFolderTree(folderTree)}

                                <div className="add-root-folder-wrapper">
                                    {addingToPath === ADD_NEW_FOLDER_KEY ? (
                                        renderAddFolderForm('')
                                    ) : (
                                        <button
                                            className="btn-link add-new-folder-link"
                                            onClick={() => setAddingToPath(ADD_NEW_FOLDER_KEY)}>
                                            Add new folder
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="buttons-container">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleClose}>
                                Cancel
                            </button>
                            <ButtonLoader
                                buttonText="Upload"
                                loaderButtonText=""
                                isLoading={isUploading}
                                disabled={!isFormValid || isUploading}
                                onClick={handleUpload}
                                className="btn btn-primary"
                            />
                        </div>
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default VDRUploadFileModal;
